import useStore from '@/lib/store';
import { zodResolver } from '@hookform/resolvers/zod';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { createColumns } from '../components/single/tables/columns';
import { createSelectedColumns } from '../components/single/tables/SelectedColumn';
import {
  ContourItem,
  TAgroTechnicalEventContourDetail,
} from '../models/IAgroTechnicalEvents';
import { agroTechnicalEventsFormSchema } from '../schema/agroTechnicalEventsSchema';
import useAgroTechnicalEventsStore from '../store/useAgroTechnicalEventsStore';

type AgroTechnicalEventsFormValues = {
  crop: string;
  value: TAgroTechnicalEventContourDetail[];
};

export default function useAgroTechnicalEventsHook() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');
  const { id } = useParams();

  const { plants } = useStore();
  const {
    agroTechnicalEventById,
    agroTechnicalEventByIdLoading,
    fetchAgroTechnicalEventById,
    fetchAgroTechnicalEventsProjects,
  } = useAgroTechnicalEventsStore();
  useEffect(() => {
    if (id) {
      fetchAgroTechnicalEventById({ id });
    }
  }, [id]);
  const options = useMemo(() => {
    return plants?.map((item) => {
      return {
        label: item.name,
        value: item.id,
      };
    });
  }, [plants]);

  const form = useForm<AgroTechnicalEventsFormValues>({
    resolver: zodResolver(agroTechnicalEventsFormSchema),
    defaultValues: {
      crop: '',
      value: [
        {
          selected: false,
        },
      ],
    },
  });

  const columns = createColumns();
  const selectedColumns = createSelectedColumns({ form, setTab });
  const { fields } = useFieldArray({
    control: form.control,
    name: 'value',
  });

  const watchedValue = useWatch({
    control: form.control,
    name: 'value',
  }) as ContourItem[];
  const crop = useWatch({ control: form.control, name: 'crop' });

  const filteredData = useMemo(() => {
    if (tab === 'all') return watchedValue;
    return watchedValue?.filter((el) => {
      if (tab === 'merged' && el.merged) return el;
      else if (tab === 'unMerged' && !el.merged && !el.selected) return el;
    });
  }, [watchedValue, tab]);

  const selectedData = useMemo(() => {
    return (watchedValue || []).filter(
      (item) => (item.selected && !item.merged) === true,
    );
  }, [watchedValue]);

  const data = useMemo(() => {
    return tab === 'selected' ? selectedData : filteredData;
  }, [tab, selectedData, filteredData]);

  const table = useReactTable({
    data: data || [],
    columns: tab === 'selected' ? selectedColumns : columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const contourList = useMemo(() => {
    return agroTechnicalEventById?.contours
      ? agroTechnicalEventById?.contours?.map((item) => {
          if (item.merged_details) {
            return { merged: true, selected: true, ...item?.merged_details };
          } else {
            return {
              merged: false,
              selected: false,
              ...item?.land_details,
              land_type: '1',
              land_extra_irragation:
                item?.land_details?.land_extra_irragation ?? '1',
              fond_type_id: item?.land_details?.fond_type_id ?? '1',
              plant_type_id: item?.land_details?.plant_type_id ?? '1',
            };
          }
        })
      : [];
  }, [agroTechnicalEventById]);
  useEffect(() => {
    form.reset({
      value: contourList as TAgroTechnicalEventContourDetail[] | [],
    });
  }, [contourList]);

  const onSubmit = async () => {
    const rawValues = form.getValues();
    const selectedContours = rawValues.value?.filter(
      (item: any) => item.selected && !item.merged,
    );

    const result = agroTechnicalEventsFormSchema.safeParse({
      crop: rawValues.crop,
      value: selectedContours,
    });

    if (!result.success) {
      toast.error(t('not.available.error'));
      return;
    }

    const values = result.data;
    const payload = {
      plant_id: Number(values.crop),
      parent_land_id: Number(agroTechnicalEventById?.id) || null,
      sub_plant_id: null,
      selected_contours: (values.value || [])
        .filter((el) => el.selected)
        .map((item, i) => ({
          contour_id: item.contour_id,
          fond_type_id: item.fond_type_id,
          land_extra_irragation: item.land_extra_irragation,
          land_type: item.land_type,
          massiv_id: item.massiv_id,
          plant_type_id: item.plant_type_id,
          key: i,
        })),
    };

    try {
      await fetchAgroTechnicalEventsProjects(payload);
      await fetchAgroTechnicalEventById({ id: String(id) });
      await setTab('all');
      await form.reset();
      await toast.success(t('create.project.success'));
    } catch (error) {
      console.error('Error creating document:', error);
    }
  };

  return {
    tab,
    form,
    open,
    fields,
    table,
    options,
    plants,
    agroTechnicalEventById,
    agroTechnicalEventByIdLoading,
    contourList,
    setTab,
    setOpen,
    onSubmit,
  };
}
